import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
  Divider,
  Avatar,
  Stack,
  Paper,
  IconButton,
  Box,
  Chip,
  Card,
  CardContent,
  CardHeader,
  alpha,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Loader } from "../../components/CustomLoader";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import {
  calculateAge,
  capitalize,
  formatNumber,
  getDeactivationInfo,
  formatDeactivationDays,
} from "../../utils/helpers";
import { fDate } from "../../utils/formatTime";
import { 
  ArrowBack, 
  Person, 
  Email, 
  Phone, 
  Cake, 
  Public, 
  Code, 
  AccountBalanceWallet,
  Login,
  CheckCircle,
  Cancel,
  PauseCircle,
  Warning,
  Diamond,
  Token
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { IMAGE_BASE } from "../../utils/constants";

const ViewUserManagement = ({ id, show, onHide }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfExclude, setSelfExclude] = useState(null);
  const navigate = useNavigate();

  const userData = async () => {
    try {
      setLoading(true);
      const response = await userServices.getById({ id });
      setData(response.data.data?.result);
      setSelfExclude(response.data.data?.selfExclude || null);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) userData();
  }, [id]);

  const getStatusChip = (status) => {
    switch (status) {
      case 1:
        return (
          <Chip
            icon={<CheckCircle />}
            label="Active"
            color="success"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
      case 0:
        return (
          <Chip
            icon={<Cancel />}
            label="Inactive"
            color="error"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
      case 3:
        return (
          <Chip
            icon={<PauseCircle />}
            label="Self Excluded"
            color="warning"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
      default:
        return (
          <Chip
            icon={<Warning />}
            label="Unknown"
            color="default"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        );
    }
  };
  const deactivation = getDeactivationInfo(data);

  const getAgeVerificationText = (status) => {
    switch (status) {
      case "notVerified":
        return "Not Verified";
      case "verified":
        return "Verified";
      case "submitted":
        return "Submitted";
      case "under18":
        return "Under 18";
      default:
        return capitalize(status) || "Not Verified";
    }
  };

  const InfoField = ({ icon, label, value, color = "text.secondary" }) => (
    <Grid item xs={12} sm={6}>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          {icon}
          <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="body2" color={color} sx={{ ml: 4 }}>
          {value || "-"}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={show}
      onClose={onHide}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
        {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Loader />
        </Box>
      ) : (
        <Box>
          {/* Header */}
          <Box sx={{ 
            p: 3, 
            borderBottom: 1, 
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              User Details
            </Typography>
            <IconButton 
              onClick={onHide}
              size="small"
            sx={{
                color: 'text.secondary',
                '&:hover': { 
                  backgroundColor: alpha('#ff4842', 0.08),
                  color: 'error.main'
                }
              }}
            >
              ✕
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ overflow: 'auto', maxHeight: 'calc(90vh - 80px)' }}>
            {/* User Profile Header */}
            <Box sx={{ 
              p: 4, 
              background: 'linear-gradient(135deg, #00a1dd 0%, #2f3366 100%)',
              color: 'white'
            }}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Avatar
                  src={IMAGE_BASE + data?.image}
                  alt={data?.firstname || 'User'}
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    border: 3, 
                    borderColor: 'white',
                    fontSize: '2rem',
                    fontWeight: 600
                  }}
                >
                  {data?.firstname?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {data?.firstname} {data?.lastname}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                    {data?.email}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Information Grid */}
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Personal Information */}
              <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        Personal Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Full Name:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {data?.firstname} {data?.lastname}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Username:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            @{data?.username}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Date of Birth:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {data?.dob ? new Date(data.dob).toLocaleDateString() : '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Age:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {data?.dob ? `${calculateAge(data.dob)} years` : '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Gender:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {capitalize(data?.gender) || '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
              </Grid>

                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        Contact Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Email:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                            {data?.email || '-'}
                          </Typography>
                        </Box>
                        {data?.phoneNumber && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Phone:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {data.countryCode} {data.phoneNumber}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Country:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {capitalize(data?.country) || '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
              </Grid>
              
                {/* Account Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        Account Information
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          {getStatusChip(data?.status)}
                        </Box>

                        {/* Deactivation waiting period - red once past the allowed days */}
                        {deactivation && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Deactivated On:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {fDate(deactivation.deactivatedAt)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Inactive for:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: deactivation.exceeded ? 700 : 500,
                                  color: deactivation.exceeded ? 'error.main' : 'text.primary',
                                }}
                              >
                                {formatDeactivationDays(deactivation.days)}
                              </Typography>
                            </Box>
                          </>
                        )}

  <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 1,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Age Verification:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {getAgeVerificationText(data?.ageVerificationStatus)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 1,
                          }}
                        ></Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Referral Code:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                            {data?.referralCode || '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Signup Bonus:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatNumber(data?.signUpBonusCredits || 0)} Credits
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Money Balance:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            £{formatNumber(data?.balance || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Gem Balance:
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Diamond sx={{ fontSize: 16, color: "#4c5bafff" }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatNumber(data?.gemBalance || 0)}
                            </Typography>
                          </Stack>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            SD coin Balance:
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Token sx={{ fontSize: 16, color: "#ffc107" }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatNumber(data?.sdCoinBalance || 0)}
                            </Typography>
                          </Stack>
                        </Box>
                          {/* Show login method used by the user */}
                          {Array.isArray(data?.socialLoginType) && data.socialLoginType.length > 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Login Method:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {data.socialLoginType
                                  .map((type) => {
                                    const lower = String(type).toLowerCase();
                                    if (lower.includes('google')) return 'Google';
                                    if (lower.includes('facebook') || lower.includes('fb')) return 'Facebook';
                                    if (lower.includes('apple')) return 'Apple';
                                    return type;
                                  })
                                  .join(', ')}
                              </Typography>
                            </Box>
                          ) : (
                            // If no social login recorded, fall back to the standard password login
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Login Method:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Email & Password
                              </Typography>
                            </Box>
                          )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Self Exclude Details */}
              {data?.status === 3 && selfExclude && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ 
                      borderColor: 'warning.main',
                      bgcolor: alpha('#FFA48D', 0.05)
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                          <Warning color="warning" />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                            Self Exclusion Details
                          </Typography>
                        </Stack>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Short Break:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selfExclude?.shortBreak ? `${selfExclude.shortBreak} days` : '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Short Break Until:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selfExclude?.shortBreakUntil ? new Date(selfExclude.shortBreakUntil).toLocaleDateString() : '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Self Exclude Period:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selfExclude?.selfExclude ? `${selfExclude.selfExclude} days` : '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Self Exclude Until:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {selfExclude?.selfExcludeUntil ? new Date(selfExclude.selfExcludeUntil).toLocaleDateString() : '-'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
              )}
            </Grid>
            </Box>

            {/* Footer */}
            <Box sx={{ 
              p: 3, 
              borderTop: 1, 
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              bgcolor: 'background.neutral'
            }}>
              <Button 
                variant="contained" 
                onClick={onHide}
                sx={{ 
                  minWidth: 100,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Box>
        )}
    </Dialog>
  );
};

export default ViewUserManagement;